import { Color3, type Mesh, MeshBuilder, StandardMaterial, Vector3 } from "@babylonjs/core";
import { HeroId, Slot, ck, hv, pk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import { brass, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { ClientHero } from "../types.ts";

const V = { combo: 0, wallCount: 1, armorPulse: 2 } as const;
const PK = { SeedBomb: pk(HeroId.Root, 0) } as const;
const CK = { ThornWall: ck(HeroId.Root, 0), IronWall: ck(HeroId.Root, 1) } as const;

const woodSwing: Cue = trim(9, (o) => {
  whoosh(o, 0.55, 180, 820, 0.24);
  thump(o, 0.35, 72, 34, 0.18);
  noise(o, { color: "brown", type: "lowpass", f: 700, gain: 0.18, d: 0.2 });
  return 0.42;
});
const seedThrow: Cue = trim(14, (o) => {
  tone(o, { f: 180, f1: 360, glide: 0.16, gain: 0.12, d: 0.22 });
  noise(o, { color: "pink", type: "bandpass", f: 900, f1: 2200, gain: 0.2, d: 0.18 });
  return 0.38;
});
const wallGrow: Cue = trim(0, (o) => {
  thump(o, 0.65, 58, 30, 0.35);
  noise(o, { color: "brown", type: "lowpass", f: 300, f1: 1200, gain: 0.7, d: 0.55 });
  return 0.9;
});

const theme = {
  bpm: 74,
  octave: -1,
  voice: brass,
  notes: [
    [0, 1.5],
    [7, 0.75],
    [5, 0.75],
    [0, 2],
  ],
} satisfies ClientHero["audio"]["theme"];

type Gear = { heart: Mesh };
const mat = (scene: Parameters<NonNullable<ClientHero["constructs"]>[number]>[0], hex: string, alpha = 1) => {
  const m = new StandardMaterial("root-mat", scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.emissiveColor = Color3.FromHexString(hex).scale(0.15);
  m.alpha = alpha;
  return m;
};

const hero: ClientHero<Gear> = {
  id: HeroId.Root,
  bulk: 1.55,
  shape: { head: 0.9, torso: 1.25, arms: 1.35, legs: 1.08 },
  gear(g) {
    const bark = g.toon("#5b3b22");
    const barkDark = g.toon("#2b1a0f");
    const leaf = g.toon("#70a64a");
    const glow = g.glow("#b7ff68", 0.7);
    for (const side of [-1, 1]) {
      const hand = side < 0 ? g.rig.handL : g.rig.handR;
      const branch = g.add(MeshBuilder.CreateCylinder("branch-arm", { diameterTop: 0.11, diameterBottom: 0.23, height: 0.92, tessellation: 7 }, g.scene), hand, bark);
      branch.rotation.x = Math.PI / 2;
      branch.position.set(0, -0.02, -0.38);
      for (let i = 0; i < 3; i++) {
        const finger = g.add(MeshBuilder.CreateCylinder("root-finger", { diameterTop: 0.035, diameterBottom: 0.075, height: 0.42, tessellation: 5 }, g.scene), hand, i === 1 ? barkDark : bark);
        finger.rotation.x = Math.PI / 2 + (i - 1) * 0.18;
        finger.rotation.y = (i - 1) * 0.22;
        finger.position.set((i - 1) * 0.11, -0.02, -0.84);
      }
      const elbowShoot = g.add(MeshBuilder.CreateCylinder("root-elbow-shoot", { diameterTop: 0.05, diameterBottom: 0.12, height: 0.48, tessellation: 5 }, g.scene), side < 0 ? g.rig.armL : g.rig.armR, barkDark);
      elbowShoot.rotation.z = side * 0.65;
      elbowShoot.position.set(side * 0.18, -0.1, 0);
    }
    for (let i = 0; i < 5; i++) {
      const crown = g.add(MeshBuilder.CreateSphere("leaf-crown", { diameter: 0.22 + (i % 2) * 0.08, segments: 8 }, g.scene), g.rig.head, leaf);
      crown.position.set((i - 2) * 0.12, 0.19 + (i % 2) * 0.05, 0.03 * Math.cos(i));
    }
    const heart = g.add(MeshBuilder.CreateSphere("root-heart", { diameter: 0.22, segments: 10 }, g.scene), g.rig.torso, glow);
    heart.position.set(0, 0.1, -0.36);
    return { heart };
  },
  animate(gear, f) {
    gear.heart.scaling.setAll(1 + (f.v[V.armorPulse] > 0 ? 0.25 : 0.08 * Math.sin(f.t * 5)));
    if (f.action === 3 || f.action === 1) return f.v[V.combo] % 2 ? "attack-melee-left" : "attack-melee-right";
    if (f.action === 4 || f.action === 5) return "interact-right";
    return f.speed > 0.5 ? "walk" : "idle";
  },
  hud(c, eid) {
    return [
      { label: "Walls", value: Math.min(1, hv(c, eid, V.wallCount) / 3), color: "#8abf48" },
      { label: "Combo", value: hv(c, eid, V.combo) / 3, color: "#b7ff68" },
    ];
  },
  projectiles: {
    [PK.SeedBomb]: (scene) => {
      const pod = MeshBuilder.CreateSphere("seed-pod", { diameter: 0.45, segments: 8 }, scene);
      pod.material = mat(scene, "#b7ff68", 0.95);
      return {
        gear: { pod },
        update({ pod }, f) {
          pod.position.set(f.pos.x, f.pos.y, f.pos.z);
          pod.rotation.y += f.dt * 8;
        },
        dispose() {
          pod.dispose();
        },
      };
    },
  },
  constructs: {
    [CK.ThornWall]: (scene) => {
      const root = MeshBuilder.CreateBox("thorn-wall", { width: 4.6, height: 3, depth: 0.55 }, scene);
      root.material = mat(scene, "#5b3b22", 0.9);
      const thorns = Array.from({ length: 10 }, (_, i) => {
        const t = MeshBuilder.CreateCylinder("thorn", { diameterTop: 0.02, diameterBottom: 0.12, height: 0.5, tessellation: 5 }, scene);
        t.material = mat(scene, i % 2 ? "#a9dc68" : "#2f7d45", 0.85);
        return t;
      });
      return {
        gear: { root, thorns },
        update({ root, thorns }: { root: Mesh; thorns: Mesh[] }, f) {
          root.position = new Vector3(f.pos.x, f.pos.y + 1.5, f.pos.z);
          root.rotation.y = f.yaw;
          root.visibility = 0.45 + f.hpFrac * 0.5;
          thorns.forEach((t, i) => {
            const x = -2.05 + i * 0.46;
            t.position = new Vector3(f.pos.x + Math.cos(f.yaw) * x, f.pos.y + 0.7 + (i % 4) * 0.42, f.pos.z - Math.sin(f.yaw) * x);
            t.rotation.y = f.yaw;
            t.rotation.x = Math.PI / 2;
            t.visibility = root.visibility;
          });
        },
        dispose: () => {
          root.dispose();
          thorns.forEach((t) => t.dispose());
        },
      };
    },
    [CK.IronWall]: (scene) => {
      const root = MeshBuilder.CreateBox("heartwood-wall", { width: 5.4, height: 3.4, depth: 0.68 }, scene);
      root.material = mat(scene, "#3e2714", 0.95);
      const heart = MeshBuilder.CreateSphere("heartwood-core", { diameter: 0.48, segments: 8 }, scene);
      heart.material = mat(scene, "#a9dc68", 0.85);
      return {
        gear: { root, heart },
        update({ root, heart }, f) {
          root.position = new Vector3(f.pos.x, f.pos.y + 1.7, f.pos.z);
          root.rotation.y = f.yaw;
          root.visibility = 0.55 + f.hpFrac * 0.45;
          heart.position = new Vector3(f.pos.x, f.pos.y + 1.75, f.pos.z - 0.36);
          heart.rotation.y = f.yaw;
          heart.visibility = root.visibility;
        },
        dispose: () => {
          root.dispose();
          heart.dispose();
        },
      };
    },
  },
  icons: {
    [Slot.Primary]: "M5 14c3-5 8-7 14-6M8 18c2-4 5-6 9-7",
    [Slot.Secondary]: "M12 3c4 4 6 7 6 10a6 6 0 0 1-12 0c0-3 2-6 6-10z",
    [Slot.Ability1]: "M5 19V8l7-4 7 4v11M8 16h8M9 11h6",
    [Slot.Ability2]: "M4 20V9l8-5 8 5v11M7 17h10M12 7v10",
    [Slot.Ultimate]: "M12 4v16M4 12h16M6 6c4 5 8 7 12 12M18 6C14 11 10 13 6 18",
    passive: "M12 21c-4-5-6-9-3-14 2 3 4 3 6 0 3 5 1 9-3 14z",
  },
  audio: {
    weight: "heavy",
    fire: (ctx) => ({ key: ctx?.alt ? "fire:root:seed" : "fire:root:swing", cue: ctx?.alt ? seedThrow : woodSwing }),
    ability: { [Slot.Ability1]: wallGrow, [Slot.Ability2]: wallGrow, [Slot.Ultimate]: wallGrow },
    theme,
    themeTrim: { heroic: -1, hostile: -7, tease: -1 },
    constructLoops: {
      [CK.ThornWall]: { build: loops.portal, gain: 0.35, range: 24 },
      [CK.IronWall]: { build: loops.beacon, gain: 0.35, range: 24 },
    },
  },
};

export default hero;
